import { useRef, useState } from 'react';
import { useIngestRuns, useRunIngest } from '../../hooks/useIngestRuns';
import { useIngestConfig } from '../../hooks/useIngestConfig';
import { useUploadIngestSource } from '../../hooks/useUploadIngestSource';
import { useToast } from '../../components/Toast';
import { DataTable, type Column } from '../../components/DataTable';
import { IngestStatusCard } from '../../components/IngestStatusCard';
import { ApiError } from '../../lib/api';
import type { IngestRunDto, QueryOptions } from '../../lib/types';

function durationLabel(run: IngestRunDto) {
  if (!run.completedUtc) return '—';
  const ms = new Date(run.completedUtc).getTime() - new Date(run.startedUtc).getTime();
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

const STATUS_PILL = {
  Succeeded: 'bg-emerald-100 text-emerald-700',
  Failed: 'bg-red-100 text-red-700',
  Running: 'bg-amber-100 text-amber-700',
};

export function IngestRunsPage() {
  const [options, setOptions] = useState<QueryOptions>({ page: 1, pageSize: 20 });
  const { latestRun, list } = useIngestRuns(options);
  const configQuery = useIngestConfig();
  const runNow = useRunIngest();
  const upload = useUploadIngestSource();
  const { success, error: toastError } = useToast();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [runAfterUpload, setRunAfterUpload] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isRunning = latestRun.data?.status === 'Running';

  async function handleRunNow() {
    try {
      const run = await runNow.mutateAsync();
      if (run.status === 'Failed') {
        toastError(run.error ?? 'Ingest failed. See run history.');
      } else {
        success(
          `Ingest ${run.status.toLowerCase()}: ${run.rowsIn} rows in, ${run.rowsLoaded} loaded.`,
        );
      }
    } catch {
      toastError('Could not trigger ingest. Try again.');
    }
  }

  async function handleUpload() {
    if (!selectedFile) return;
    try {
      const result = await upload.mutateAsync({ file: selectedFile, runNow: runAfterUpload });
      if (result.run?.status === 'Failed') {
        toastError(`Uploaded, but ingest failed: ${result.run.error ?? 'unknown error'}`);
      } else if (result.run) {
        success(
          `Uploaded ${result.fileName}. Ingest ${result.run.status.toLowerCase()}: ${result.run.rowsIn} rows in, ${result.run.rowsLoaded} loaded.`,
        );
      } else {
        success(`Uploaded ${result.fileName}. Click Run now to ingest.`);
      }
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      const message =
        err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body
          ? String((err.body as { error: unknown }).error)
          : 'Upload failed. Try again.';
      toastError(message);
    }
  }

  const columns: Column<IngestRunDto>[] = [
    { header: 'Started', render: (r) => new Date(r.startedUtc).toLocaleString() },
    { header: 'Duration', render: (r) => durationLabel(r) },
    {
      header: 'Status',
      render: (r) => (
        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_PILL[r.status]}`}>
          {r.status}
        </span>
      ),
    },
    {
      header: 'Trigger',
      render: (r) => (
        <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{r.triggerKind}</span>
      ),
    },
    { header: 'Rows In', render: (r) => r.rowsIn.toLocaleString() },
    { header: 'Loaded', render: (r) => r.rowsLoaded.toLocaleString() },
    {
      header: 'Error',
      render: (r) =>
        r.error ? (
          <span className="text-xs text-red-700" title={r.error}>
            {r.error.slice(0, 60)}{r.error.length > 60 ? '…' : ''}
          </span>
        ) : null,
    },
  ];

  return (
    <section className="bg-white rounded-lg shadow p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-brand-blue">Ingest</h1>
        <p className="text-sm text-brand-charcoal/70">Schedule data ingestion from source.</p>
      </div>

      <IngestStatusCard
        latestRun={latestRun.data ?? null}
        sourceMode={configQuery.data?.source ?? '…'}
        cadenceMinutes={configQuery.data?.cadenceMinutes ?? 0}
        onRunNow={() => void handleRunNow()}
        isRunning={isRunning}
      />

      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/40">
        <h2 className="text-base font-semibold text-brand-charcoal">Upload test data</h2>
        <p className="text-xs text-brand-charcoal/70 mt-1">
          Replace the active xlsx source file. Must match the <code className="font-mono">Jobs with Crew Planning Report</code> column layout. Up to 50 MB.
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
            className="text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-brand-blue file:text-white file:text-sm file:font-medium hover:file:bg-brand-blue/90"
          />
          <label className="flex items-center gap-2 text-sm text-brand-charcoal">
            <input
              type="checkbox"
              checked={runAfterUpload}
              onChange={(e) => setRunAfterUpload(e.target.checked)}
              className="rounded"
            />
            Run ingest after upload
          </label>
          <button
            type="button"
            onClick={() => void handleUpload()}
            disabled={!selectedFile || upload.isPending}
            className="bg-brand-orange text-white rounded px-4 py-1.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-orange/90"
          >
            {upload.isPending ? 'Uploading…' : 'Upload'}
          </button>
          {selectedFile && (
            <span className="text-xs text-brand-charcoal/60">
              {selectedFile.name} · {(selectedFile.size / 1024).toFixed(0)} KB
            </span>
          )}
        </div>
      </div>

      <h2 className="text-lg font-semibold text-brand-charcoal mt-8">Run History</h2>

      <DataTable
        data={list.data}
        isLoading={list.isLoading}
        error={list.error}
        options={options}
        onOptionsChange={setOptions}
        columns={columns}
        rowKey={(r) => r.id}
        emptyState="No ingest runs yet. Click Run now to trigger the first run."
      />
    </section>
  );
}
